/**
 * V2 Options List Style Configuration
 *
 * Factory functions for style section configs adapted to V2 schema paths.
 * V2 uses nested semantic structure instead of V1's flat CSS-like properties:
 *   - V1: list.styles.borderWidth → V2: content.options.appearance.borders.width
 *   - V1: list.styles.paddingTop → V2: content.options.appearance.insets.top
 *   - V1: list.items.0.title.styles.fontSize → V2: content.options.items.0.title.appearance.fontSize
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import type { ListStyleSectionConfig } from '../types/listSection.types';
import {
  NUMBER_0_50_OPTIONS,
  NUMBER_0_500_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  V2_TEXT_ALIGN_OPTIONS,
  SCALE_MODE_OPTIONS,
  BOOLEAN_OPTIONS,
  CHECKBOX_KIND_OPTIONS,
} from './atomicFields';
import { GRID_LAYOUT_VISIBILITY } from './gridItemLayoutPresets';
import { createListGradientEditorComponent } from '../components/shared/styleFields/gradientEditorFactory';

/**
 * Section id of the list "Animations" style section — drives the generated test ids
 * (`checkbox-style-animations-<label>`). The section's fields are V2ListAnimationFields,
 * passed in by the editor (see createV2OptionsListStyleSections): importing that
 * component here would pull @modules/ui-components (import.meta via guides) into every
 * pure consumer of this config.
 */
export const LIST_ANIMATION_SECTION_ID = 'animations';

export interface V2OptionsListStyleSectionsOptions {
  /**
   * Renders the "Animations" section (V2ListAnimationFields). Omitted → no Animations
   * section (pure config consumers such as item-default derivation never need it).
   */
  animationFields?: ListStyleSectionConfig['customComponent'];
}

// ============================================
// Vertical Alignment Options (same as V1)
// ============================================

const VERTICAL_ALIGNMENT_OPTIONS = [
  { value: 'top', label: 'top' },
  { value: 'center', label: 'center' },
  { value: 'bottom', label: 'bottom' },
] as const;

const SECTION_HEIGHT_KIND_OPTIONS = [
  { value: 'fixed', label: 'Fixed (px)' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'auto', label: 'Auto' },
] as const;

const NUMBER_0_100_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
  value: `${i * 5}`,
  label: `${i * 5}`,
}));

const CHECKBOX_POSITION_OPTIONS = [
  { value: 'topStart', label: 'Top Start' },
  { value: 'topEnd', label: 'Top End' },
  { value: 'bottomStart', label: 'Bottom Start' },
  { value: 'bottomEnd', label: 'Bottom End' },
] as const;

// ============================================
// V2 List Position Fields
// ============================================

/**
 * Create V2 list position fields (verticalAlignment + useMaxCellHeight)
 * V2 paths: content.options.appearance.{verticalAlignment, useMaxCellHeight}
 */
function createV2ListPositionFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.verticalAlignment`,
      label: 'vertical alignment',
      type: 'select',
      options: [...VERTICAL_ALIGNMENT_OPTIONS],
      defaultValue: 'center',
      isNullable: true,
      guideKey: 'screenedit-options-vertical-alignment',
    },
    {
      path: `${basePath}.appearance.useMaxCellHeight`,
      label: 'max cell height',
      type: 'select',
      options: [...BOOLEAN_OPTIONS],
      defaultValue: false,
      isNullable: true,
      booleanValue: true,
      guideKey: 'screenedit-options-max-cell-height',
    },
  ];
}

// ============================================
// V2 Item State Fields (borders use nested structure)
// ============================================

/**
 * Create V2 item state fields (background + borders)
 * V2 uses nested borders: {width, color, radius.uniform}
 * instead of V1's flat: {borderWidth, borderColor, borderRadius}
 *
 * @param basePath - e.g. 'content.options.appearance' or 'content.options.selectedAppearance'
 */
function createV2ItemStateFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.backgroundColor`,
      label: 'background color',
      type: 'color',
      defaultValue: '#ffc201',
      isNullable: true,
    },
    {
      path: `${basePath}.borders.width`,
      label: 'border width',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 1,
      defaultScrollValue: 1,
      isNullable: true,
    },
    {
      path: `${basePath}.borders.color`,
      label: 'border color',
      type: 'color',
      defaultValue: '#606060',
      isNullable: true,
    },
    {
      path: `${basePath}.borders.radius.uniform`,
      label: 'border radius',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 12,
      defaultScrollValue: 12,
      isNullable: true,
    },
  ];
}

// ============================================
// V2 Text Style Fields (.appearance instead of .styles)
// ============================================

/**
 * Create V2 text style fields for title/subtitle
 * V2 uses .appearance.{fontSize, fontWeight, color, align}
 * instead of V1's .styles.{fontSize, fontWeight, color, textAlign}
 *
 * @param basePath - e.g. 'content.options.items.0.title'
 */
export function createV2TextStyleFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.fontFamily`,
      label: 'font family',
      type: 'select',
      options: [...FONT_FAMILY_OPTIONS],
      defaultValue: 'SF Pro',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.fontSize`,
      label: 'font size',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 15,
      defaultScrollValue: 18,
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.fontWeight`,
      label: 'font weight',
      type: 'select',
      options: [...FONT_WEIGHT_OPTIONS_SHORT],
      defaultValue: 400,
      isNullable: true,
      numericValue: true,
    },
    {
      path: `${basePath}.appearance.color`,
      label: 'text color',
      type: 'color',
      defaultValue: '#000000',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.align`,
      label: 'text align',
      type: 'select',
      options: [...V2_TEXT_ALIGN_OPTIONS],
      defaultValue: 'center',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.lineHeight`,
      label: 'line height',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultScrollValue: 18,
      isNullable: true,
    },
    // Background group
    {
      path: `${basePath}.appearance.backgroundColor`,
      label: 'background color',
      type: 'color',
      defaultValue: '#000000',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.backgroundColorOpacity`,
      label: 'background opacity',
      type: 'numberCombo',
      options: Array.from({ length: 11 }, (_, i) => ({
        value: `${(i * 0.1).toFixed(1)}`,
        label: `${Math.round(i * 10)}%`,
      })),
      defaultValue: 1,
      defaultScrollValue: 1,
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.backgroundCornerRadius`,
      label: 'background radius',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultScrollValue: 0,
      isNullable: true,
    },
  ];
}

// ============================================
// V2 Image Style Fields (.appearance.dimensions instead of .styles)
// ============================================

/**
 * Create V2 image style fields
 * V2 uses .appearance.dimensions.{width, height} and .appearance.cornerRadii.uniform
 * instead of V1's .styles.{width, height, mainCornerRadius}
 *
 * @param basePath - e.g. 'content.options.items.0.image'
 */
export function createV2ImageStyleFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.dimensions.width`,
      label: 'image-width',
      type: 'numberCombo',
      options: NUMBER_0_500_OPTIONS,
      defaultScrollValue: 100,
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.dimensions.height`,
      label: 'image-height',
      type: 'numberCombo',
      options: NUMBER_0_500_OPTIONS,
      defaultScrollValue: 100,
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.scaleMode`,
      label: 'image-scale-mode',
      type: 'select',
      options: [...SCALE_MODE_OPTIONS],
      defaultValue: 'scaleAspectFit',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.cornerRadii.uniform`,
      label: 'corner-radius',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultScrollValue: 20,
      isNullable: true,
    },
  ];
}

// ============================================
// V2 Checkbox Style Fields
// ============================================

/**
 * Create V2 checkbox style fields
 * V2 uses .selectedAppearance.color and .appearance.color
 * instead of V1's .selectedBlock.styles.color and .styles.color
 *
 * @param basePath - e.g. 'content.options.items.0.checkbox'
 */
function createV2CheckboxStyleFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.kind`,
      label: 'shape',
      type: 'select',
      options: [...CHECKBOX_KIND_OPTIONS],
      defaultValue: 'square',
      isNullable: false,
      guideKey: 'screenedit-options-checkbox-styles',
    },
    {
      path: `${basePath}.selectedAppearance.color`,
      label: 'checked color',
      type: 'color',
      defaultValue: '#ffc201',
      isNullable: true,
    },
    {
      path: `${basePath}.appearance.color`,
      label: 'unchecked color',
      type: 'color',
      defaultValue: '#ffffff',
      isNullable: true,
    },
  ];
}

// ============================================
// Items Spacing Field
// ============================================

/**
 * Create V2 items spacing field
 * V2-only field (not present in V1)
 */
function createV2ItemsSpacingFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.appearance.itemsSpacing`,
      label: 'spacing',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultValue: 8,
      defaultScrollValue: 8,
      isNullable: true,
      guideKey: 'screenedit-options-items-spacing',
    },
  ];
}

// ============================================
// Main Factory
// ============================================

/**
 * Create V2 options list style sections
 *
 * Returns 14 collapsible sections:
 * 1. Selection behavior (isRandomized + maxSelectedItemCount)
 * 2. List position (verticalAlignment + useMaxCellHeight)
 * 3. Unselected item state (backgroundColor + borders + gradient)
 * 4. Selected item state (backgroundColor + borders + gradient)
 * 5. Item paddings (appearance.insets)
 * 6. List paddings (layout.insets)
 * 7. Items spacing (V2-only)
 * 8. Cell dimensions (grid layouts only)
 * 9. Image container (twoCols only: verticalAlignment, heightKind, heightValue + insets)
 * 10. Text container (twoCols only: verticalAlignment, heightKind, heightValue + insets)
 * 11. Checkbox container (twoCols only: position + margin)
 * 12. Title styles (fontSize, fontWeight, color, align + padding)
 * 13. Subtitle styles (conditional on itemLayout)
 * 14. Image styles (conditional on itemLayout)
 * 15. Checkbox styles (conditional on itemLayout)
 * 16. Animations (always last, when options.animationFields is given; item entrance +
 *     stagger, selection/deselection feedback — V2ListAnimationFields writes each
 *     nullable object whole, in one change)
 *
 * Padding sections use `paddingConfig.basePath` pointing to V2 insets paths.
 * V2ListStyleSections component handles the translation from
 * PaddingElement's paddingTop/Right/Bottom/Left → V2's top/end/bottom/start.
 */
export function createV2OptionsListStyleSections(
  options: V2OptionsListStyleSectionsOptions = {},
): ListStyleSectionConfig[] {
  const optionsBase = 'content.options';

  return [
    // Selection behavior (isRandomized + maxSelectedItemCount)
    {
      id: 'selectionBehavior',
      title: 'Selection behavior',
      defaultExpanded: true,
      fields: [
        {
          path: `${optionsBase}.isRandomized`,
          label: 'randomize order',
          type: 'toggle' as const,
          defaultValue: false,
          isNullable: false,
          guideKey: 'screenedit-options-randomize-order',
        },
        {
          path: `${optionsBase}.maxSelectedItemCount`,
          label: 'max selections',
          type: 'number' as const,
          defaultValue: 2,
          isNullable: true,
          min: 1,
          max: 100,
          guideKey: 'screenedit-options-max-selections',
        },
      ],
    },
    // List position
    {
      id: 'listPosition',
      title: 'List position',
      defaultExpanded: true,
      fields: createV2ListPositionFields(optionsBase),
    },
    // Unselected item state (includes gradient editor)
    {
      id: 'unselectedItemState',
      title: 'Unselected item state',
      defaultExpanded: true,
      fields: createV2ItemStateFields(`${optionsBase}.appearance`),
      customComponent: createListGradientEditorComponent(`${optionsBase}.appearance.gradient`),
    },
    // Selected item state (includes gradient editor)
    {
      id: 'selectedItemState',
      title: 'Selected item state',
      defaultExpanded: true,
      fields: createV2ItemStateFields(`${optionsBase}.selectedAppearance`),
      customComponent: createListGradientEditorComponent(`${optionsBase}.selectedAppearance.gradient`),
    },
    // Item paddings (V2: appearance.insets)
    {
      id: 'itemPaddings',
      title: 'Item paddings',
      defaultExpanded: false,
      fields: [],
      paddingConfig: {
        basePath: `${optionsBase}.appearance.insets`,
        label: 'Item padding',
      },
    },
    // List paddings (V2: layout.insets)
    {
      id: 'listPaddings',
      title: 'List paddings',
      defaultExpanded: false,
      fields: [],
      paddingConfig: {
        basePath: `${optionsBase}.layout.insets`,
        label: 'List padding',
      },
    },
    // Items spacing (V2-only)
    {
      id: 'itemsSpacing',
      title: 'Items spacing',
      defaultExpanded: false,
      fields: createV2ItemsSpacingFields(optionsBase),
    },
    // Cell dimensions (visible only for grid item layouts)
    {
      id: 'cellDimensions',
      title: 'Cell dimensions',
      defaultExpanded: false,
      fields: [
        {
          path: `${optionsBase}.cellHeightFromCellWidth`,
          label: 'Cell Height (% of width)',
          type: 'number',
          min: 30,
          max: 300,
          step: 5,
          defaultValue: 100,
          isNullable: true,
          guideKey: 'screenedit-options-cell-dimensions',
        },
      ],
      visibilityCondition: (itemType: string) =>
        GRID_LAYOUT_VISIBILITY[itemType]?.showCellDimensions ?? false,
    },
    // Image container (visible only for grid/twoCols screens)
    {
      id: 'imageContainer',
      title: 'Image container',
      defaultExpanded: false,
      fields: [
        {
          path: `${optionsBase}.appearance.imageContainer.verticalAlignment`,
          label: 'vertical align',
          type: 'select',
          options: [...VERTICAL_ALIGNMENT_OPTIONS],
          defaultValue: 'top',
          isNullable: true,
          guideKey: 'screenedit-options-image-container',
        },
        {
          path: `${optionsBase}.appearance.imageContainer.contentAlignment`,
          label: 'content align',
          type: 'select',
          options: [...VERTICAL_ALIGNMENT_OPTIONS],
          defaultValue: 'center',
          isNullable: true,
        },
        {
          path: `${optionsBase}.appearance.imageContainer.heightKind`,
          label: 'height kind',
          type: 'select',
          options: [...SECTION_HEIGHT_KIND_OPTIONS],
          defaultValue: 'percent',
          isNullable: true,
        },
        {
          path: `${optionsBase}.appearance.imageContainer.heightValue`,
          label: 'height value',
          type: 'numberCombo',
          options: NUMBER_0_100_OPTIONS,
          defaultValue: 70,
          defaultScrollValue: 70,
          isNullable: true,
        },
      ],
      paddingConfig: {
        basePath: `${optionsBase}.appearance.imageContainer.insets`,
        label: 'Container padding',
      },
      visibilityCondition: (itemType: string, context?) =>
        context?.columnLayout === 'twoCols' && (GRID_LAYOUT_VISIBILITY[itemType]?.showImageContainer ?? true),
    },
    // Text container (visible only for grid/twoCols screens with image-based layouts)
    {
      id: 'textContainer',
      title: 'Text container',
      defaultExpanded: false,
      fields: [
        {
          path: `${optionsBase}.appearance.textContainer.verticalAlignment`,
          label: 'vertical align',
          type: 'select',
          options: [...VERTICAL_ALIGNMENT_OPTIONS],
          defaultValue: 'bottom',
          isNullable: true,
          guideKey: 'screenedit-options-text-container',
        },
        {
          path: `${optionsBase}.appearance.textContainer.contentAlignment`,
          label: 'content align',
          type: 'select',
          options: [...VERTICAL_ALIGNMENT_OPTIONS],
          defaultValue: 'center',
          isNullable: true,
        },
        {
          path: `${optionsBase}.appearance.textContainer.heightKind`,
          label: 'height kind',
          type: 'select',
          options: [...SECTION_HEIGHT_KIND_OPTIONS],
          defaultValue: 'percent',
          isNullable: true,
        },
        {
          path: `${optionsBase}.appearance.textContainer.heightValue`,
          label: 'height value',
          type: 'numberCombo',
          options: NUMBER_0_100_OPTIONS,
          defaultValue: 30,
          defaultScrollValue: 30,
          isNullable: true,
        },
      ],
      paddingConfig: {
        basePath: `${optionsBase}.appearance.textContainer.insets`,
        label: 'Container padding',
      },
      visibilityCondition: (itemType: string, context?) =>
        context?.columnLayout === 'twoCols' && (GRID_LAYOUT_VISIBILITY[itemType]?.showTextContainer ?? true),
    },
    // Checkbox container (visible only for grid/twoCols screens)
    {
      id: 'checkboxContainer',
      title: 'Checkbox container',
      defaultExpanded: false,
      fields: [
        {
          path: `${optionsBase}.appearance.checkboxContainer.enabled`,
          label: 'show checkbox',
          type: 'toggle',
          defaultValue: false,
          isNullable: false,
          guideKey: 'screenedit-options-checkbox-container',
        },
        {
          path: `${optionsBase}.appearance.checkboxContainer.position`,
          label: 'position',
          type: 'select',
          options: [...CHECKBOX_POSITION_OPTIONS],
          defaultValue: 'topEnd',
          isNullable: true,
        },
      ],
      paddingConfig: {
        basePath: `${optionsBase}.appearance.checkboxContainer.margin`,
        label: 'Checkbox margin',
      },
      visibilityCondition: (itemType: string, context?) =>
        context?.columnLayout === 'twoCols' && (GRID_LAYOUT_VISIBILITY[itemType]?.showCheckboxContainer ?? true),
    },
    // Title styles
    {
      id: 'titleStyles',
      title: 'Title styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${optionsBase}.items.0.title`),
      paddingConfig: {
        basePath: `${optionsBase}.items.0.title.layout.insets`,
        label: 'Title padding',
      },
    },
    // Subtitle styles (visible when itemLayout includes 'Subtitle')
    {
      id: 'subtitleStyles',
      title: 'Subtitle styles',
      defaultExpanded: false,
      fields: createV2TextStyleFields(`${optionsBase}.items.0.subtitle`),
      paddingConfig: {
        basePath: `${optionsBase}.items.0.subtitle.layout.insets`,
        label: 'Subtitle padding',
      },
      visibilityCondition: (itemType: string) => itemType.includes('Subtitle'),
    },
    // Image styles (visible when itemLayout includes 'Image')
    {
      id: 'imageStyles',
      title: 'Image styles',
      defaultExpanded: false,
      fields: createV2ImageStyleFields(`${optionsBase}.items.0.image`),
      paddingConfig: {
        basePath: `${optionsBase}.items.0.image.layout.insets`,
        label: 'Image padding',
      },
      visibilityCondition: (itemType: string) => itemType.includes('Image'),
    },
    // Checkbox styles are meaningful only when the item layout renders a
    // checkbox. Grid overlay placement remains owned by checkboxContainer.
    {
      id: 'checkboxStyles',
      title: 'Checkbox styles',
      defaultExpanded: false,
      fields: createV2CheckboxStyleFields(`${optionsBase}.items.0.checkbox`),
      paddingConfig: {
        basePath: `${optionsBase}.items.0.checkbox.layout.insets`,
        label: 'Checkbox padding',
      },
      visibilityCondition: (itemType: string) => itemType.includes('Checkbox'),
    },
    // Animations — always the last section, for every item layout.
    ...(options.animationFields
      ? [{
          id: LIST_ANIMATION_SECTION_ID,
          title: 'Animations',
          defaultExpanded: false,
          fields: [],
          customComponent: options.animationFields,
        }]
      : []),
  ];
}
